import { getPassioFoodDisplayName, getPassioLocalizationConfigForBrandedSearch, type PassioFoodDataInfo } from "@notemeal/passio-utils";
import { useMutation } from "@apollo/client";
import { useCallback, useState } from "react";
import { useLocaleContext } from "shared-ui/contexts/Locale/LocaleContext";
import {
  AddPassioFoodByRefCodeDocument,
  AddPassioFoodByRefCodeMutation,
  AddPassioFoodByRefCodeMutationVariables,
  FoodWithDefaultServingFragment,
} from "../types";

export type AddPassioFoodFromSearchFailureReason = "missing_ref_code" | "mutation_failed";

export type AddPassioFoodFromSearchResult =
  | { success: true; food: FoodWithDefaultServingFragment }
  | { success: false; reason: AddPassioFoodFromSearchFailureReason; message: string };

export const useAddPassioFoodFromSearch = () => {
  const { locale: orgLocale } = useLocaleContext();
  const [addPassioFoodByRefCode] = useMutation<AddPassioFoodByRefCodeMutation, AddPassioFoodByRefCodeMutationVariables>(
    AddPassioFoodByRefCodeDocument
  );
  const [loading, setLoading] = useState(false);

  const addFromPassioSearchResult = useCallback(
    async (foodData: PassioFoodDataInfo): Promise<AddPassioFoodFromSearchResult> => {
      const refCode = foodData.refCode;
      if (!refCode) {
        return {
          success: false,
          reason: "missing_ref_code",
          message: "Unable to add this food. Please try a different item.",
        };
      }

      setLoading(true);
      try {
        let result: Awaited<ReturnType<typeof addPassioFoodByRefCode>>;
        try {
          result = await addPassioFoodByRefCode({
            variables: {
              input: {
                passioItemRefCode: refCode,
                name: getPassioFoodDisplayName(foodData),
                brandName: foodData.brandName ?? null,
                originSource: foodData.originSource ?? null,
                appLocale: orgLocale,
                foodSearchLocale: getPassioLocalizationConfigForBrandedSearch(orgLocale).productLocalization,
              },
            },
          });
        } catch {
          return {
            success: false,
            reason: "mutation_failed",
            message: "Failed to add food. Please try again or choose a different food.",
          };
        }

        const createdFood = result.data?.addPassioFoodByRefCode?.food;
        if (!createdFood) {
          return {
            success: false,
            reason: "mutation_failed",
            message: "Unable to add food. Please try again or choose a different food.",
          };
        }
        return { success: true, food: createdFood as FoodWithDefaultServingFragment };
      } finally {
        setLoading(false);
      }
    },
    [addPassioFoodByRefCode, orgLocale]
  );

  return { addFromPassioSearchResult, loading };
};
